// React Dependencies
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';

// External Dependencies
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import MaterialTable from 'material-table';
import { toast } from 'react-toastify';
import { connect } from 'react-redux';
import { MenuItem, Select } from '@material-ui/core';

// Internal Dependencies
import MaterialDatePicker from '../../../../components/Common/form/MaterialDatePicker';
import UpdatePromoCodeModal from './UpdatePromoCodeModal';
import { Status } from '../../../../common/constants';
import { IsEmpty } from '../../../../common/checks';

function PromoCodesTable(props, ref) {
  const { gifts } = props.promoCode;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPromoCode, setSelectedPromoCode] = useState(null);

  const tableRef = useRef(null);

  const refreshPromoCodes = () => {
    if (!IsEmpty(tableRef.current)) {
      tableRef.current.onQueryChange();
    }
  };

  useImperativeHandle(ref, () => ({
    refreshPromoCodes,
  }), []);

  const onUpdateModalClose = () => {
    setIsModalOpen(false);
  };

  const columns = useMemo(() => [
    {
      title:    'Code',
      field:    'code',
      editable: 'never',
      sorting:  false,
    },
    {
      title:           'Gift ID',
      field:           'giftId',
      editable:        'never',
      sorting:         false,
      cellStyle:       { width: '25%' },
      filterComponent: (filterProps) => (
        <Select
          className="w-100"
          variant="standard"
          style={{ maxWidth: 200 }}
          onChange={(e) => filterProps.onFilterChanged(filterProps.columnDef.tableData.id, e.target.value)}
          customFilterAndSearch={(filterTerm, rowData) => {
            if (!filterTerm) return true;
            return rowData.giftId === filterTerm;
          }}
        >
          <MenuItem value="" style={{ fontSize: 14 }}>
            <em>None</em>
          </MenuItem>
          {
            !IsEmpty(gifts) && (
              _.map(gifts, (gift) => (
                <MenuItem key={gift.id} style={{ fontSize: 14 }} value={gift.id}>
                  {gift.label}
                  {' '}
                  -
                  {' '}
                  {gift.id}
                </MenuItem>
              ))
            )
          }
        </Select>
      ),
    },
    {
      title:           'Status',
      field:           'status',
      editable:        'never',
      sorting:         false,
      filterComponent: (filterProps) => (
        <Select
          className="w-100"
          onChange={(e) => filterProps.onFilterChanged(filterProps.columnDef.tableData.id, e.target.value)}
        >
          <MenuItem value="">-</MenuItem>
          <MenuItem value={Status.Activated}>{Status.Activated}</MenuItem>
          <MenuItem value={Status.Unredeemed}>{Status.Unredeemed}</MenuItem>
          <MenuItem value={Status.Redeemed}>{Status.Redeemed}</MenuItem>
          <MenuItem value={Status.Deactivated}>{Status.Deactivated}</MenuItem>
        </Select>
      ),
      customFilterAndSearch: (filterTerm, rowData) => {
        if (!filterTerm) return true;
        return rowData.status === filterTerm;
      },
    },
    {
      title:           'Expiry Date',
      field:           'expiryDate',
      editable:        'never',
      sorting:         false,
      filterComponent: (filterProps) => (
        <MaterialDatePicker
          minDate={false}
          onChange={(date) => filterProps.onFilterChanged(filterProps.columnDef.tableData.id, date)}
          variant="outlined"
          clearable
        />
      ),
      customFilterAndSearch: (filterTerm, rowData) => {
        if (!filterTerm) return true;

        const rowDate = new Date(rowData.expiryDate);
        const filterDate = new Date(filterTerm);

        return (
          rowDate.getFullYear() === filterDate.getFullYear()
          && rowDate.getMonth() === filterDate.getMonth()
          && rowDate.getDate() === filterDate.getDate()
        );
      },
      render: (row) => moment(row.expiryDate).utcOffset('+08', false).format('DD MMM YYYY'),
    },
    {
      title:           'Created At',
      field:           'createdAt',
      editable:        'never',
      sorting:         false,
      filterComponent: (filterProps) => (
        <MaterialDatePicker
          minDate={false}
          onChange={(date) => filterProps.onFilterChanged(filterProps.columnDef.tableData.id, date)}
          variant="outlined"
          clearable
        />
      ),
      customFilterAndSearch: (filterTerm, rowData) => {
        if (!filterTerm) return true;

        const rowDate = new Date(rowData.createdAt);
        const filterDate = new Date(filterTerm);

        return (
          rowDate.getFullYear() === filterDate.getFullYear()
          && rowDate.getMonth() === filterDate.getMonth()
          && rowDate.getDate() === filterDate.getDate()
        );
      },
      render: (row) => moment(row.createdAt).utcOffset('+08', false).format('DD MMM YYYY, HH:mm:ss'),
    },
    {
      title:           'Last Updated At',
      field:           'updatedAt',
      editable:        'never',
      sorting:         false,
      filterComponent: (filterProps) => (
        <MaterialDatePicker
          minDate={false}
          onChange={(date) => filterProps.onFilterChanged(filterProps.columnDef.tableData.id, date)}
          variant="outlined"
          clearable
        />
      ),
      customFilterAndSearch: (filterTerm, rowData) => {
        if (!filterTerm) return true;

        const rowDate = new Date(rowData.updatedAt);
        const filterDate = new Date(filterTerm);

        return (
          rowDate.getFullYear() === filterDate.getFullYear()
          && rowDate.getMonth() === filterDate.getMonth()
          && rowDate.getDate() === filterDate.getDate()
        );
      },
      render: (row) => moment(row.updatedAt).utcOffset('+08', false).format('DD MMM YYYY, HH:mm:ss'),
    },
  ], [gifts]);

  return (
    <>
      <MaterialTable
        title=""
        tableRef={tableRef}
        isLoading={props.promoCode.showLoadingOverlay}
        actions={[
          (row) => ({
            icon:     'edit',
            tooltip:  'Change Status',
            disabled: row.status !== Status.Unredeemed,
            onClick:  () => {
              if (row.status === Status.Unredeemed) {
                setIsModalOpen(true);
                setSelectedPromoCode({
                  id:         row.id,
                  status:     row.status,
                  expiryDate: row.expiryDate,
                });
              }
            },
            iconProps: {
              style: {
                cursor: 'pointer',
                color:  (row.status === Status.Unredeemed) && 'black',
              },
            },
          }),
        ]}
        columns={columns}
        data={props.fetchPromoCodes}
        options={{
          filtering:           true,
          toolbar:             false,
          emptyRowsWhenPaging: false,
          pageSizeOptions:     [5, 10, 25, 50, 100],
          actionsColumnIndex:  -1,
          debounceInterval:    500,
          actionsCellStyle:    {
            width: '8%',
          },
        }}
      />
      {
        selectedPromoCode && (
          <UpdatePromoCodeModal
            isModalOpen={isModalOpen}
            onClose={onUpdateModalClose}
            selectedPromoCode={selectedPromoCode}
            pageId={props.page.selected.id}
            mode="Single"
            onComplete={() => {
              try {
                toast.success('Promo code updated successfully');
                refreshPromoCodes();
              } catch (error) {
                toast.error(error?.message || 'Failed to fetch promo codes');
              }
            }}
          />
        )
      }
    </>
  );
}

PromoCodesTable.propTypes = {
  page: PropTypes.shape({
    selected: PropTypes.shape({
      id: PropTypes.number,
    }),
  }).isRequired,
  promoCode: PropTypes.shape({
    promoCodes:         PropTypes.arrayOf(PropTypes.shape({})),
    gifts:              PropTypes.arrayOf(PropTypes.shape({})),
    showLoadingOverlay: PropTypes.bool,
  }).isRequired,

  fetchPromoCodes: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  page:      state.page,
  promoCode: state.promoCode,
});

export default connect(mapStateToProps, null, null, { forwardRef: true })(
  forwardRef(PromoCodesTable),
);
